// Every write this app makes, and the one place a refusal is noticed.
//
// ── Refusals (#162) ─────────────────────────────────────────────────────────
//
// A sync chip that counts unacknowledged writes would say "Saved" for a write
// the server refused, because a refused write stops being unacknowledged
// exactly as a successful one does: `localStoreRejectBatch` drops the mutation from the
// queue, recalculates the overlays, and the document reverts to whatever the
// server has — so `hasPendingWrites` falls to zero and the falling edge flashes
// Syncing, then Saved, for an edit that is gone.
//
// The pending-write count therefore cannot answer the question on its own. What
// can is the promise: `batch.commit()` and `setDoc()` reject when the server
// permanently refuses, and that is the signal this file turns into an event.
//
// ── Why it is not caught at the call sites ──────────────────────────────────
//
// A refusal can arrive long after the write: offline, a commit settles only
// when the network comes back, by which time the drawer that made it has
// unmounted. So it is observed centrally, outside any component's lifetime,
// by the module-level subscription below: `SyncContext` subscribes once for
// the life of the app.
//
// ── The facade (#126) ───────────────────────────────────────────────────────
//
// Every data module routes every write through here, so there is one seam.
// `writeBatch(db)`, `.set`, `.update`, `.delete`, `.commit()`, `setDoc`,
// `deleteDoc` keep the SDK's signatures, and `serverTimestamp()` imported from
// `firebase/firestore` works — see `opCodec.js` for how a sentinel is
// recognised.
//
// Underneath, a commit does not go to the SDK directly. It is materialised,
// written to a durable queue (`queue.js`, `queueStore.js`) and dispatched from
// there, under rules that live in the Rust core (#125).
//
// ── The one behaviour that differs from the SDK's ──────────────────────────
//
//     The promise settles when the write is **acknowledged or durably owed,
//     whichever comes first.**
//
// Online that is the SDK's behaviour: dispatched at once, resolved on the
// server's acknowledgement, rejected on a permanent refusal. Offline (#108),
// an awaited commit that never settled would leave a drawer waiting forever
// and skip cleanup such as #94's receipt orphan sweep.
//
// **And offline the write still goes to the SDK.** Withheld by the queue, the
// mutation would enter no `onSnapshot` listener on the snapshot read arm and
// the teacher's own edit would vanish off their own screen (#237).
// `dispatchTimeoutMs` bounds how long the *caller* waits, not how long the
// write is given. `queue.js`'s header carries the argument.
//
// So **a refusal that arrives after the promise resolved cannot reject it.**
// It reaches `onWriteRefused` instead, which `SyncContext`, the chip and
// #167's durable record already rely on.
//
// ── Not read off the snapshots ──────────────────────────────────────────────
//
// A refused write reverts its document, but comparing data before and after
// cannot tell refusal from success: almost every write carries
// `serverTimestamp()`, null locally and a real timestamp once the server has
// it, and a second device writing the same row fools it the other way. The
// promise is the signal.
//
// ── What the promise cannot catch, and what does ────────────────────────────
//
// **A write that outlives the tab that made it.** The persisted mutation queue
// survives a reload; the callbacks do not. The SDK says so itself, in
// `processUserCallback`: "Mutations restored from persistence won't have
// callbacks, so it's okay for there to be no callback for this ID." So a write
// queued offline, a page reload, then a reconnect that refuses it, rejects
// nothing here. Signing out and back in loses the callbacks the same way.
//
// That includes the overnight case, where a studio's `accessOpen` window
// closes between the write and the reconnect.
//
// **`src/data/outstandingWrites.js` is the durable half** (#167). Every wrapper
// below notes the write there *before* issuing it and clears the note when the
// queue reaches a terminal answer. A record still present when the SDK's queue
// has drained is a write whose callback did not survive, which `SyncContext`
// reports rather than rounding to "Saved". The same records are the chip's
// pending count (#169, #170) — see that file's header for why the count cannot
// come from query listeners.
//
// **The note is cleared on the queue's terminal answer, not on the caller's
// promise.** The promise resolves as soon as the write is owed; clearing on it
// would drop a still-queued write out of the chip's count and say "Up to date"
// over work that has not been sent. So the queue reports settlement separately
// (`onSettled`) and this file listens to that.
//
// Multi-tab: a write made in one tab and refused while another holds the
// primary lease still rejects in the tab that made it, via
// `syncEngineApplyBatchState`.

import {
  doc,
  arrayRemove as fsArrayRemove,
  arrayUnion as fsArrayUnion,
  deleteField as fsDeleteField,
  serverTimestamp as fsServerTimestamp,
  writeBatch as fsWriteBatch,
  Timestamp,
} from 'firebase/firestore';
import { COLLECTIONS } from './paths';
import {
  recordWrite, clearWrite, clearWriteForBatch, outstandingWrites, releaseBatchCopy,
  detachFollowOn,
} from './outstandingWrites';
import { loadCore } from '../lib/core';
import { createQueue, OUTCOME, isLiveWrite } from './queue';
import { defaultQueueStore } from './queueStore';
import { liteWritesEnabled } from './liteWriteFlag';
import { dispatchViaLite } from './liteDispatch';
import { KIND, TRANSFORM, decodeFields, encodeFields } from './opCodec';
import { freeId } from '../domain/freeId';

/**
 * The sentinels, re-exported.
 *
 * `serverTimestamp` and `deleteField` are the SDK's own: `opCodec.js` can
 * recognise and reconstruct those from the class alone, so importing either
 * from here or from `firebase/firestore` is the same thing.
 *
 * **`arrayUnion` and `arrayRemove` are ours and are not interchangeable.** Their
 * elements are private to the SDK's instance, so a queued write built from the
 * SDK's version could not be reconstructed after a reload — the codec refuses
 * either by name and says to import ours instead. One call site each:
 * `useEvents.jsx` on `eventSeries.exceptions`, and `useTags.jsx` on tag
 * membership (#198, #288).
 */
export { serverTimestamp, deleteField, arrayUnion, arrayRemove } from './opCodec';

/**
 * What the chip calls the thing that was refused.
 *
 * A collection name is not a word a teacher uses, so the ones a teacher would
 * recognise are translated and the rest are left out — a refusal we cannot
 * name says "a change was refused" rather than "an eventSeries change was
 * refused".
 */
const SUBJECTS = {
  [COLLECTIONS.students]: 'student',
  [COLLECTIONS.families]: 'family',
  [COLLECTIONS.eventSeries]: 'lesson',
  [COLLECTIONS.occurrences]: 'lesson',
  [COLLECTIONS.attendance]: 'attendance',
  [COLLECTIONS.ledgerEntries]: 'billing',
  [COLLECTIONS.makeupEntries]: 'make-up',
  [COLLECTIONS.packageEntries]: 'package',
  [COLLECTIONS.expenseEntries]: 'expense',
  [COLLECTIONS.expenseCategories]: 'category',
  [COLLECTIONS.categories]: 'category',
  [COLLECTIONS.locations]: 'location',
  [COLLECTIONS.tags]: 'tag',
  [COLLECTIONS.noteTemplates]: 'note template',
  [COLLECTIONS.messageTemplates]: 'message template',
  [COLLECTIONS.files]: 'file',
  [COLLECTIONS.resourceFolders]: 'folder',
  [COLLECTIONS.practiceEntries]: 'practice',
  [COLLECTIONS.repertoire]: 'repertoire',
  [COLLECTIONS.mileageEntries]: 'mileage',
  [COLLECTIONS.settings]: 'settings',
};

/** The collection segment of a `users/{uid}/{collection}/{id}` path. */
function collectionOfPath(path) {
  return String(path || '').split('/').slice(-2, -1)[0] || null;
}

/**
 * The collection noun for a document reference, or null.
 *
 * Reads `parent.id` where the SDK offers it and falls back to the path, since
 * every reference this app builds is `users/{uid}/{collection}/{id}` and the
 * second-to-last segment is the collection either way.
 */
export function subjectForRef(ref) {
  const name = ref?.parent?.id || collectionOfPath(ref?.path);
  return (name && SUBJECTS[name]) || null;
}

/** The same question asked of a bare path, which is what the queue holds. */
function subjectForPath(path) {
  const name = collectionOfPath(path);
  return (name && SUBJECTS[name]) || null;
}

/**
 * One subject for a set of references, or null where they disagree.
 *
 * A batch is usually one row's worth of writes — a student and its family, a
 * charge and its credit — so this is normally unanimous. Where it is not, the
 * chip says less rather than naming whichever ref was added first.
 */
function subjectForRefs(refs) {
  let found = null;
  for (const ref of refs) {
    const subject = subjectForRef(ref);
    if (!subject) return null;
    if (found === null) found = subject;
    else if (found !== subject) return null;
  }
  return found;
}

const listeners = new Set();

/**
 * Be told when a write dies.
 *
 * The listener receives `{ code, message, subject, reason }` — `subject` being
 * a noun from the table above, or null, and `reason` the core's word for how it
 * died: `refused` (the server said no) or `exhausted` (the retry budget ran
 * out). Returns its own unsubscribe.
 *
 * **`refused` is the SDK's own judgement rather than ours**: `RemoteStore` retries a write stream error
 * unless `isPermanentWriteError` says otherwise, and only then rejects the
 * callback. The three places the SDK resolves a mutation callback are an
 * acknowledgement, a rejection, and the multi-tab relay of one of those — so a
 * rejection always means the server said no, never that the network is flaky.
 *
 * `exhausted` is the queue's and is a different fact: nobody said no, the
 * queue stopped asking. A teacher acts on the two differently, so they arrive
 * distinguishable, as they are kept apart inside the core (#125).
 */
export function onWriteRefused(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function report(refusal, paths) {
  // A refusal reaches a teacher as one short chip label; whoever they then ask
  // needs the code and the path.
  console.error('[sync] write refused:', refusal.code, paths, refusal.message);
  listeners.forEach((listener) => {
    try {
      listener(refusal);
    } catch (err) {
      // A subscriber that throws must not stop the next one being told, and
      // must not turn a refusal into a second, unrelated failure.
      console.error('[sync] refusal listener threw:', err);
    }
  });
}

// ── The queue, and the four things it needs that it refuses to import ──────
//
// `queue.js` imports no Firebase at all, so that the `ui` test project can
// drive it with no emulator. This is where the real clock, the real
// network and the real SDK are supplied.

// The `Firestore` handle, captured from the first call that carries one.
//
// Every call site already passes it (`writeBatch(db)`) or carries it on a
// `DocumentReference`, and this app has exactly one — `src/lib/firebase.js`
// creates a single instance. Captured rather than imported because importing it
// would pull `import.meta` into every test project that touches a write, which
// is the #112 parse error and the reason `integration` has to map that module
// away.
//
// ── Capturing it lazily is not enough (#277) ───────────────────────────────
//
// **A batch restored from IndexedDB has no call site** (#277). It is replayed
// by the queue, from a row, in a session where the teacher may not have saved
// anything — close the laptop offline, open it somewhere with signal. With no
// handle, `fsWriteBatch(null)` throws, the queue reads a thrown dispatch as
// *transient*, and the batch spends its retry budget on a bug in this file.
// So `startWriteQueue` takes the handle, and `dispatch` refuses to pretend
// otherwise if it somehow still does not have one.
//
// ── This handle is the full SDK's, and lite's is a different one (#410) ────
//
// `rememberFirestore(ref?.firestore)` below captures a **full-SDK** handle off
// a caller's `DocumentReference`. `firebase/firestore/lite` registers itself
// as a separate component (`firestore/lite`), so both SDKs live on one
// `FirebaseApp` and hand out *different* `Firestore` objects, and lite refuses
// the other one's:
//
//     lite.writeBatch(fullSdkFirestore)
//     → FirebaseError invalid-argument: "Type does not match the expected
//       instance. Did you pass a reference from a different Firestore SDK?"
//
// That is a **throw inside `dispatch`**, which the queue reads as transient —
// the #277 shape above. So `dispatch` takes the lite branch before it reads
// `firestore` at all.
//
// Lite's own handle comes from `src/data/liteDispatch.js`:
// `getFirestore(getApp())`, which needs only `firebase/app` — no
// `import.meta`, so no #112. A caller's ref is the wrong SDK's, and most
// integration suites never call `startWriteQueue` (see `sessionKnown` below).
// So that module knows where a handle comes from, and the emulator is wired on
// the lite instance separately in `src/lib/firebase.js` **and**
// `test/integration/firebaseTestApp.js`.
let firestore = null;
/** Said once per session — see `dispatch`. */
let warnedAboutHandle = false;

/**
 * The uid this tab is signed in as, or null for *signed out*.
 *
 * **Supplied rather than read, for the same reason the handle is** — this
 * module imports no Firebase client. `SyncContext` passes it to
 * `startWriteQueue` alongside `db`, keyed on the user, so a sign-in replaces it.
 *
 * Meaningless until `sessionKnown` is true; read the two together.
 */
let sessionUid = null;
/**
 * Whether anybody has *told* this module who is signed in.
 *
 * `sessionUid = null` alone would mean both *signed out* and *nobody has
 * said*, and a guard in `dispatch` reading it as the first would hold **every**
 * write in a session that never calls `startWriteQueue`: the integration
 * suites that reach `save(...)` through a harness mounting no `SyncProvider`,
 * and the app's own window between mount and `onAuthStateChanged` answering
 * (#282).
 *
 * So the two cases are two values. Unknown dispatches, which every caller that
 * is not `SyncProvider` relies on. Known-and-different holds, which is #282's
 * protection, and `SyncProvider` waits for auth to resolve before it says
 * anything (see there).
 */
let sessionKnown = false;
/** `startWriteQueue`'s default second argument: "I am not saying who." */
const NO_CLAIM = Symbol('no claim about who is signed in');
/** Batch ids already refused for belonging to somebody else. Said once each. */
const foreign = new Set();
const rememberFirestore = (handle) => { if (handle && !firestore) firestore = handle; };

// **There is no `batchId → note id` map here (#382).** A module-scope map
// works only for batches this tab enqueued: a batch the queue *replays* after
// a reload settles in a tab whose map is empty, so the durable note would
// survive the write landing and the chip would report a change on the server
// as unconfirmed (save offline, reload, come back online).
//
// The note carries its own batch id (`recordWrite`), so the pairing is as
// durable as the two things it pairs. See `clearWriteForBatch`.

const SENTINEL_FACTORIES = {
  [TRANSFORM.serverTimestamp]: fsServerTimestamp,
  [TRANSFORM.deleteField]: fsDeleteField,
  [TRANSFORM.arrayUnion]: fsArrayUnion,
  [TRANSFORM.arrayRemove]: fsArrayRemove,
  // The only factory here that is handed a stored value rather than
  // reconstructing a sentinel from nothing: every dispatch of the row rebuilds
  // the *same* instant, so a redelivery does not change `createdAt` and
  // `createdAtUnchanged()` does not refuse it (#232).
  [TRANSFORM.enqueuedAt]: (ms) => Timestamp.fromMillis(ms),
};

/**
 * Whose studio a batch writes into, or null if it does not say.
 *
 * Every path this app writes is `users/{uid}/{collection}/{id}`. A batch is one
 * row's worth of writes and does not span two studios, so disagreement here is
 * a reason to refuse to guess.
 */
function studioOf(record) {
  let found = null;
  for (const op of record.ops) {
    const uid = String(op.path || '').split('/')[1] || null;
    if (!uid) return null;
    if (found === null) found = uid;
    else if (found !== uid) return null;
  }
  return found;
}

/**
 * Hand one queued batch to the SDK.
 *
 * **Every rejection is reported as permanent, which is the SDK's judgement.**
 * `batch.commit()` rejects only when `isPermanentWriteError` has already said
 * retrying cannot help; a transient failure leaves the promise unsettled
 * instead, which the queue's own timeout turns into `unreachable`.
 *
 * **That is a fact about `firebase/firestore`, not about Firestore** (#410):
 * the full SDK holds an unsendable write in its own durable mutation queue and
 * leaves the promise pending. Lite's `writeBatch().commit()` is a single REST
 * call that *rejects promptly* on an unreachable server, and calling that
 * permanent would dead-letter every write made offline. So the lite path turns
 * on `classifyCommitError` in `src/data/liteDispatch.js`, which carries the
 * code table.
 *
 * `liteWriteFlag.js` is `false` by default (#410). No test takes a lite write
 * offline and back: lite has no `disableNetwork`.
 *
 * What else changes when writes leave the SDK's queue is on #207 and #410.
 */
async function dispatch(record) {
  // ── A batch belonging to the teacher who signed out (#282) ────────────────
  //
  // `AuthContext.logout()` is a bare `signOut(auth)`: it clears neither
  // `coda-write-queue` (IndexedDB, one per origin) nor the outstanding-write
  // ledger. If teacher A saves offline and B then signs in on the same laptop,
  // the reclaim at mount would dispatch A's batch under B's credentials;
  // `firestore.rules` refuses it, the queue dead-letters it as **permanent**,
  // and B's chip reports a refusal for a change B never made.
  //
  // So the guard is here, at the one place a batch meets the network.
  // `unreachable`, the same arm as a missing handle: parked, no retry spent,
  // nothing destroyed, and it goes out unchanged the moment A signs back in.
  //
  // **It holds on a *known* uid only** — see `sessionKnown`. `SyncProvider`
  // does not call `startWriteQueue` until `loading` is false, so this module is
  // never asked to judge a batch in the pre-auth window, and a session nobody
  // has spoken to at all dispatches.
  //
  // The ledger has no uid in it, so B's chip still *counts* A's writes as
  // pending (#282).
  const studio = studioOf(record);
  if (sessionKnown && studio && studio !== sessionUid) {
    if (!foreign.has(record.batchId)) {
      foreign.add(record.batchId);
      console.warn(
        `[writes] batch ${record.batchId} writes into \`users/${studio}\`, and this session is `
        + `${sessionUid ? `signed in as \`${sessionUid}\`` : 'signed out'}, so it is being held `
        + 'rather than sent (#282). It is durable, unsent, and not refused by anybody. Two cases '
        + 'this cannot tell apart: a write made by whoever was signed in before, which will go '
        + 'out when they sign back in on this browser; and a path built wrongly by a caller, '
        + 'which would be a bug here and would look identical.',
      );
    }
    return { outcome: OUTCOME.unreachable };
  }

  // ── Lite (#410) ───────────────────────────────────────────────────────────
  //
  // Everything lite needs that is different — the handle, the sentinel
  // instances a lite batch will accept, and the error classifier — is in
  // `src/data/liteDispatch.js`. The guard above runs first, because whose
  // studio a batch writes into is a question about the batch rather than the
  // transport.
  //
  // **Asked per dispatch rather than captured** (see `liteWriteFlag.js`): a
  // batch enqueued under lite is replayed by whichever dispatcher the
  // replaying session has, so the read-store rollback takes the write path
  // back with it.
  //
  // `dispatchViaLite` never throws: a throw here is read by the queue as
  // transient and spends a real write's retry budget on a bug (#277).
  if (liteWritesEnabled()) return dispatchViaLite(record);

  if (!firestore) {
    // Without this, `fsWriteBatch(null)` throws a `TypeError`, the queue reads
    // a thrown dispatch as *transient*, and a replay spends its retry budget on
    // a bug in this module, eventually dead-lettering a good write.
    //
    // `unreachable` instead, the queue's word for "no answer": no retry is
    // spent, the caller is released, the chip goes on counting the write as
    // owed, and the batch goes out unchanged the moment a handle appears. On
    // the full SDK this and the #282 guard above are the only producers of
    // `unreachable`; `startWriteQueue(db)` should make this one unreachable.
    //
    // Said once: it polls on the queue's base backoff while it lasts.
    if (!warnedAboutHandle) {
      warnedAboutHandle = true;
      console.error(
        '[writes] no Firestore handle, so a queued batch cannot be dispatched. This session has '
        + 'replayed a stored batch with no call site having supplied one and no '
        + '`startWriteQueue(db)`. The write is durable and unsent — it is **not** refused by the '
        + 'server, and no retry has been spent. It will go out as soon as a handle arrives.',
      );
    }
    return { outcome: OUTCOME.unreachable };
  }
  const batch = fsWriteBatch(firestore);
  for (const op of record.ops) {
    const ref = doc(firestore, op.path);
    if (op.kind === KIND.delete) {
      batch.delete(ref);
      continue;
    }
    const data = decodeFields(
      record.fields.filter((f) => f.opId === op.id), SENTINEL_FACTORIES,
    );
    if (op.kind === KIND.merge) batch.set(ref, data, { merge: true });
    else batch.set(ref, data);
  }
  try {
    await batch.commit();
    return { outcome: OUTCOME.acknowledged };
  } catch (error) {
    return { outcome: OUTCOME.permanent, error };
  }
}

let queue = null;

/**
 * When this copy of the module loaded: the line between notes this page wrote
 * and notes an earlier page left behind (#585). `SyncContext` draws the same
 * line for #167 with its own `SESSION_STARTED_AT`; this one is `let` only so
 * that `__resetWritesForTests` can stand in for a reload.
 */
let loadedAt = Date.now();
/** The one recovery this page runs — see `recoverUnqueuedWrites`. */
let recovering = null;

/**
 * Does a queue store outlive the page? Asked once per store.
 *
 * `indexeddb` does; `memory` and `memory-degraded` (#208) do not. Asked of the
 * store rather than assumed, because the answer decides whether a note's copy
 * of its batch may be dropped once the queue holds the batch: on a memory store
 * the note is the only thing a reload would leave, so the copy stays until the
 * batch settles.
 *
 * **Bound to the store, not kept at module scope**, because a late `onDurable`
 * from a queue that has since been replaced must not answer for its successor
 * (across `__resetWritesForTests`, for one).
 */
function durabilityOf(store) {
  let answer = null;
  return () => {
    if (!answer) answer = store.describe().then((d) => d?.kind === 'indexeddb', () => false);
    return answer;
  };
}
/** The current queue's `durabilityOf`. Replaced with the queue. */
let storeIsDurable = () => Promise.resolve(false);

/**
 * Who wants to know that the queue moved.
 *
 * A `Set` at module scope rather than a context, because the queue is one per
 * tab and its listeners outlive any React tree. `createQueue` already announces
 * every transition it makes — enqueue, dispatch, settle, reclaim, retry,
 * discard — and the read path listens (#207 U4): it has to re-read when an
 * unsent write appears or lands, because the overlay is derived from the queue
 * on every read and nothing else would tell a mounted provider that the
 * derivation now gives a different answer.
 *
 * ── It carries a payload, and it is a hint about what to skip (#389) ──────
 *
 * Without one, every transition wakes every mounted provider, and most re-read
 * a collection the write never touched — each a real IndexedDB round trip and
 * wasm crossing, three times per saved lesson (#379).
 *
 * The payload is **not** a diff of what is owed, which would be a second
 * source of truth. A woken listener re-derives the entire queue as it would
 * with no payload. What it is told is which documents the transition touched,
 * so that a listener whose answer **provably cannot have changed** may decline
 * to ask. `overlay.readCollection` folds only the ops
 * whose path sits under `users/{uid}/{collection}/` — it filters on nothing
 * else — so a transition naming no such path cannot move that collection's
 * value.
 *
 * `null` is the other half and the one to get right: it means *this transition
 * could not be enumerated*, and a listener must treat it as "everything". Being
 * over-inclusive costs a wasted read; being under-inclusive is a teacher's own
 * save not appearing, which is #237.
 */
const queueListeners = new Set();

/**
 * Tell me when the queue moves. Returns its own unsubscribe.
 *
 * The listener takes `change` — `{ paths: Set<string> }` of absolute document
 * paths, or `null` for a transition whose extent is unknown. **`null` means
 * re-read**, not "nothing moved". `changeTouches` in `src/data/overlay.js` is
 * the one place that interprets it, and it lives there rather than here
 * because the reason a skip is sound is a fact about the fold — and because
 * the two read hooks that ask must not import this file, which reaches
 * Firebase and would break the `ui` project's no-Firebase guarantee.
 *
 * A missed call costs a screen that is one write behind until the next
 * snapshot, not a wrong screen: the read itself always re-derives.
 */
export function onQueueChange(listener) {
  queueListeners.add(listener);
  return () => queueListeners.delete(listener);
}

/**
 * Hand one queue transition to this tab's listeners.
 *
 * The queue's `{ paths }` becomes a `Set` once per transition rather than once
 * per listener, since the membership test is what they all do with it. `null`
 * is forwarded as `null`. One listener throwing must not stop the next one
 * hearing about it.
 */
function tellListeners(change) {
  const forwarded = change && Array.isArray(change.paths)
    ? { paths: new Set(change.paths) }
    : null;
  queueListeners.forEach((listener) => {
    try {
      listener(forwarded);
    } catch (err) {
      console.error('[writes] queue change listener threw:', err);
    }
  });
}

// ── Sibling tabs, told when this tab's queue moves (#423) ──────────────────
//
// **The full SDK does this for us, and lite does not.** `src/lib/firebase.js`
// builds the full SDK with `persistentMultipleTabManager`, so a write one tab
// hands it lands in a mutation queue every tab of this origin shares, and
// every other tab's `onSnapshot` fires with the change applied — offline
// included. That snapshot is what wakes a sibling's read store. A lite
// `commit()` enters no SDK queue, so under lite a sibling hears of the write
// only from the server's echo, and offline there is no echo.
//
// The *data* is not the gap. Our queue is one IndexedDB database per
// origin, and `overlay.js` reads it on every read, so a sibling that re-reads
// already folds the other tab's batch. What it lacks is a reason to re-read:
// `onQueueChange` is fed by this tab's queue only. So the transition is
// posted on a `BroadcastChannel` and a sibling forwards it to its own
// listeners exactly as if its own queue had moved — the same `{ paths }`
// hint, the same `null`-means-everything rule (`changeTouches`).
//
// **Posted only while lite dispatches.** On the full SDK the shared mutation
// queue already wakes the sibling, and a second wake per transition would be
// the churn of #379. Received always, because which transport the *other* tab
// is on is that tab's business.
//
// A context with no `BroadcastChannel` — jsdom, an old browser — tells no
// sibling, and nothing breaks.
const SIBLING_CHANNEL = 'coda-write-queue/changes';
let siblings = null;

function openSiblingChannel() {
  if (siblings || typeof globalThis.BroadcastChannel !== 'function') return;
  try {
    siblings = new globalThis.BroadcastChannel(SIBLING_CHANNEL);
  } catch (err) {
    // What it saw: the constructor refused. Nothing to fall back to — the
    // sibling simply is not told.
    console.warn('[writes] could not open the sibling-tab channel (#423):', err);
    siblings = null;
    return;
  }
  siblings.onmessage = (event) => {
    const paths = event?.data?.paths;
    tellListeners(Array.isArray(paths) ? { paths } : null);
  };
}

function tellSiblingTabs(change) {
  if (!siblings || !liteWritesEnabled()) return;
  try {
    siblings.postMessage({ paths: change && Array.isArray(change.paths) ? change.paths : null });
  } catch (err) {
    console.warn('[writes] could not tell sibling tabs the queue moved (#423):', err);
  }
}

/** The queue, built on first use so that nothing happens at import time. */
function theQueue() {
  if (queue) return queue;
  openSiblingChannel();
  const store = defaultQueueStore();
  const durable = durabilityOf(store);
  storeIsDurable = durable;
  queue = createQueue({
    store,
    loadCore,
    dispatch,
    // One listener throwing must not stop the next one hearing about it, and
    // must not take the queue's own transition down with it — `queue.js`
    // already wraps this call, but its catch is for the whole set at once.
    // See `tellListeners`, and `tellSiblingTabs` for the other tabs.
    onChange: (change) => {
      tellListeners(change);
      tellSiblingTabs(change);
    },
    // **No connectivity signal and no ordering barrier** — `queue.js`'s header
    // argues each. The queue dispatches offline because a write withheld from
    // the SDK cannot appear in any snapshot listener (#237); there is no
    // barrier because handing every write to the SDK's own FIFO orders them
    // more strongly than waiting for that FIFO to empty first (#199).
    subjectForPath,
    // **The one ordering difference between the two dispatchers (#423).** The
    // full SDK sends this device's batches on one stream in the order they
    // were handed over, so the core's per-document rule is enough; a lite
    // `commit()` is its own REST call and has no order with any other, so the
    // queue takes the core's studio-order rule while lite is what dispatches.
    // Asked per round, as `dispatch` asks per batch, so the two cannot
    // disagree about which transport a batch is going out on.
    inStudioOrder: liteWritesEnabled,
    onRefused: (refusal) => report(refusal, refusal.batchId),
    // Answered, either way — `done`, `dead` or `discarded`. Cleared by batch
    // id out of the durable ledger rather than through anything this tab
    // remembers, because the tab that settles a batch is not always the tab
    // that made it (#382).
    //
    // **And a follow-on goes, or is dropped, first (#590).** A batch saved
    // with `commit({ followOn })` carries the next batch on its note; the
    // settlement is the moment that next batch may be queued — `done` — or
    // must not be — `dead` or `discarded`. Before the note is cleared, because
    // the follow-on is on it.
    onSettled: (batchId, state) => {
      releaseFollowOn(batchId, state);
      clearWriteForBatch(batchId);
    },
    // The queue's record is on disk, so the note's copy of the batch has done
    // its job (#585) — unless the disk is a `Map`, in which case it has not.
    // Two-argument `then`: the second handler is for `describe()` failing,
    // and a copy left behind costs space rather than a write.
    onDurable: (batchId) => {
      durable().then((yes) => { if (yes) releaseBatchCopy(batchId); }, () => {});
    },
  });
  return queue;
}

/** The queue, for the surfaces that report on it. Exported for #126's chip. */
export function writeQueue() {
  return theQueue();
}

/**
 * Has this device a write to this document that the server has not answered?
 *
 * ── Why a reader needs this (#345) ──────────────────────────────────────────
 *
 * A writer deciding whether its write **creates** a document has to know
 * whether one is there to merge into. `useDocumentWindow` answers that as
 * `exists`, and on the **store** arm the answer already counts a creation this
 * device has queued, because the overlay folds the queue over what the server
 * last said. The **snapshot** arm cannot: `onSnapshot` reports the server's
 * document, our writes live in this queue rather than in the SDK's, and after a
 * reload the provider has forgotten it ever made one. Without this, offline,
 * save, reload, save again would send a whole record composed from
 * `normaliseStudio(null)` — **defaults** over every field the first write had
 * set — on the snapshot arm (`test/ui/settingsCompose.test.jsx`).
 *
 * `path` is `users/{uid}/{collection}/{docId}`, as `Op.path` records it.
 *
 * **What it answers and what it does not.** It is true when a live op — one
 * that may still land, `isLiveWrite` in `./queue.js` — names this path. It is
 * not a claim that the document exists at the server, and it cannot distinguish
 * a queued creation from a queued edit of a document that was already there;
 * both are *there will be a document at this path*, which is the question a
 * writer is asking. A `dead` op is not live: a refused write left nothing
 * behind, and a caller must be free to create.
 *
 * It reads the queue's own storage and nothing else — no network, no drain —
 * so it is the same cost as a render-time read of `records()`.
 */
export async function hasLiveWriteTo(path) {
  if (!path) return false;
  const records = await theQueue().records();
  return (records || []).some(
    (record) => isLiveWrite(record)
      && (record.ops || []).some((op) => op.path === path && op.kind !== KIND.delete),
  );
}

/**
 * The `online` listener, and the undo for it.
 *
 * Module-scope rather than per-caller: the queue is one per tab and so is this.
 */
let stopWatchingConnectivity = null;

/**
 * Start the queue working, and keep it working across a reconnect (#277).
 *
 * **Two separate holes.**
 *
 * 1. **A queue restored from disk has to be started.** `theQueue()` is built
 *    on first use, which can be the first *write* of the session. A batch left
 *    in `coda-write-queue` by a tab that closed offline would sit there until
 *    the teacher happened to save something else. Since the queue dispatches
 *    offline, the SDK has its own copy and the write itself does land — but
 *    our record would stay `inflight` forever, counted by the chip, folded
 *    over the read path by the overlay, and blocking every later batch on its
 *    documents by FIFO. `drain()` here lets `reclaimStranded` see it and
 *    re-issue it.
 * 2. **Something besides the chip has to react to `online`.**
 *    `SyncContext`'s `online` listener updates `syncMachine` and nothing
 *    else.
 *
 * **What the listener is and is not for.** The queue is not told whether the
 * device is online — it dispatches either way. What connectivity is good for
 * here is narrower: a batch that was *answered* badly is sitting out a backoff,
 * and a backoff measuring a dead network should not be waited out once the
 * network is alive. Android's `Writes.track` is the same nudge.
 *
 * **Under lite it is what lands an offline write (#410).** The full SDK leaves
 * an unsendable commit's promise pending, so an offline write sits `inflight`
 * and no backoff is measuring anything. `dispatchViaLite` rejects promptly
 * instead, `classifyCommitError` calls that `unreachable`, and the queue's
 * park for that escalates to five minutes — which is why `nudge()` clears the
 * `unreachable` park rather than only re-running the loop. Why it is `nudge`
 * and not `wake` is in `queue.js`.
 *
 * `navigator.onLine` is optimistic — true on a network with no route out — so
 * a draining round it provokes may find nothing reachable. That costs a
 * dispatch that does not answer, which is the state this queue is built for.
 *
 * Idempotent, and returns nothing: the listener lives for the tab.
 */
export function startWriteQueue(handle, uid = NO_CLAIM) {
  // **The handle.** A batch restored from IndexedDB has no call site to learn
  // it from, so without this a replay reaches `dispatch` with `firestore` null
  // — see that function.
  rememberFirestore(handle);
  // The uid (#282): the queue is one per origin and outlives a sign-out, so
  // `dispatch` has to be able to tell whose work a batch is.
  //
  // **Passing one is the act that makes the answer known**, `null` for signed
  // out included. Until somebody does, `dispatch` sends — see `sessionKnown`.
  // So `SyncProvider` must not call this before auth has resolved.
  //
  // The default is a sentinel rather than `= null`: under a null default
  // `startWriteQueue(db)` would *declare this session signed out* and hold
  // every inherited batch. A caller has to write `null` to say signed out.
  if (uid !== NO_CLAIM) {
    if (uid !== sessionUid || !sessionKnown) foreign.clear();
    sessionUid = uid;
    sessionKnown = true;
  }
  const q = theQueue();
  if (!stopWatchingConnectivity && typeof globalThis.addEventListener === 'function') {
    const onUp = () => {
      // **`nudge` and not `drain` (#298).** `drain()` also means "a session is
      // claiming this queue" and clears the flag `endSession` sets — so this
      // listener, firing between a sign-out and `SyncProvider`'s
      // `startWriteQueue(db, null)`, would re-arm the timers and let a due
      // batch through to credentials that had just gone. Coming back online is
      // a fact about the device, not a claim; see `ended` in queue.js.
      //
      // `nudge` does release the `unreachable` park, which a lite batch parked
      // for want of a network is waiting on. It leaves `ended` alone and skips
      // the release entirely when the session is over.
      //
      // Two-argument `then`: the rejection handler is for the drain's own
      // failure. The loop does not throw for an ordinary write failure, so
      // anything arriving here is a bug in it.
      q.nudge().then(undefined, (err) => console.error('[writes] the reconnect drain failed:', err));
    };
    globalThis.addEventListener('online', onUp);
    stopWatchingConnectivity = () => globalThis.removeEventListener('online', onUp);
  }
  // **`drain` here, unlike the listener above**, so #298's flag cannot wedge
  // the queue. `SyncProvider` calls this once auth has resolved —
  // `startWriteQueue(db, null)` included, the pass immediately after a
  // sign-out — so this is a session claiming the queue, and clearing `ended`
  // is the point of it.
  //
  // Not awaited. The caller is a provider mounting; it has nothing to do with
  // the answer, and a drain offline takes `dispatchTimeoutMs` to return.
  //
  // **After `recoverUnqueuedWrites` (#585).** A batch an earlier page noted
  // and never stored is put back first, so this drain — the
  // claim that says whose session it is — is what sends it. The recovery never
  // rejects, so the rejection handler stays the drain's own.
  recoverUnqueuedWrites().then(() => q.drain()).then(
    undefined,
    (err) => console.error('[writes] the startup drain failed:', err),
  );
}

/**
 * Note a write as outstanding, and hand back the id that clears it.
 *
 * Called *before* the record reaches the queue: `localStorage.setItem` is
 * synchronous, so a record written before the
 * durable enqueue has landed by the time it is made, and a tab that dies
 * between the two still leaves the record behind.
 */
function note(batchId, refs, batch, followOn = null) {
  return recordWrite({
    paths: refs.map((r) => String(r?.path || '')).filter(Boolean),
    subject: subjectForRefs(refs),
    // The note's batch id (#382), written into the record itself before the
    // batch is durable, so a tab that only ever *replays* this batch can still
    // find the note it settles.
    batchId,
    // **The write itself, until the queue has it (#585).** This note is
    // synchronous and the queue's record is not, so for the length of an
    // IndexedDB put this is the only durable trace of the write — and a page
    // that went away in that window would leave a note that could only be
    // reported, never sent. `recoverUnqueuedWrites` sends it; `onDurable`
    // drops the copy once the queue's record exists.
    batch,
    // The batch to queue once this one is acknowledged (#590) — see
    // `releaseFollowOn`. Kept until this batch settles, not until it is
    // stored: it is waiting on the server's answer, not on the disk.
    followOn,
  });
}

/**
 * Queue a batch that `encodeBatch` already encoded, noting it first.
 *
 * The one path a follow-on takes into the queue, from a settlement or from a
 * later page's recovery. Returns the queue's own promise — acknowledged or
 * owed — with a handler already on it, so that a follow-on nobody is waiting
 * for (another page's, a replay's) is logged rather than unhandled; a refusal
 * also reaches the chip through `onRefused`, as any write's does.
 */
function enqueueEncoded({ batchId, ops, fields, subject, paths }) {
  const id = recordWrite({
    paths, subject, batchId, batch: { ops, fields, subject },
  });
  const queued = theQueue().enqueue({ batchId, ops, fields, subject });
  queued.then(undefined, (error) => {
    // Refused before it was owed — a defect the core found. Nothing is owed,
    // so the note goes, exactly as `enqueue` does for a caller.
    if (error?.code === 'coda/queue-defect') clearWrite(id);
    console.error(`[writes] the follow-on batch ${batchId} was not delivered:`, error);
  });
  return queued;
}

/**
 * Batches whose caller is still awaiting `commit()`, and the follow-ons that
 * caller should wait for too (#590).
 *
 * Online, a Save's promise means *both are in* — the attendance and the
 * messages — so a refused message shows under the Save button: when the batch
 * is acknowledged while its caller is still waiting, the caller waits for the
 * follow-on as well. A
 * caller already released — offline, past `dispatchTimeoutMs` — has nothing to
 * wait on, and the follow-on goes when the batch is answered, as above.
 */
const awaitingCaller = new Set();
const followOnWaits = new Map();

/**
 * A batch settled: queue what was waiting on it, or say why it will not go.
 *
 * ── What a follow-on is for (#590) ──────────────────────────────────────────
 *
 * Taking attendance asks for messages — a receipt for the payment, the lesson
 * notes, a cancellation notice — and those are written **after** the
 * attendance batch rather than inside it: a receipt for a payment that failed
 * to save is a receipt for nothing, and a message the outbox rules refuse must
 * not take the charge down with it, which one atomic batch would.
 *
 * "After the caller's promise" is not enough: that resolves on the
 * acknowledgement or after `dispatchTimeoutMs`, and a page load in that window
 * — a reload, a typed URL — would take the messages with it while the
 * attendance, durable (#585), went on to land. So the follow-on is **written
 * onto the attendance batch's own note**, synchronously with the batch itself,
 * and the settlement, in whatever page or tab it happens, queues it. `done` sends it; `dead` and `discarded` do not,
 * because the save it was about did not happen.
 *
 * Taken off the note before it is queued, so a second tab settling the same
 * batch finds nothing to send twice. Both happen in one synchronous block, so
 * no page load can fall between them.
 */
function releaseFollowOn(batchId, state) {
  const followOns = detachFollowOn(batchId);
  if (!followOns.length) return;
  if (state === 'done') {
    const queued = Promise.all(followOns.map(enqueueEncoded));
    if (awaitingCaller.has(batchId)) followOnWaits.set(batchId, queued);
    return;
  }
  console.warn(
    `[writes] batch ${batchId} was ${state === 'dead' ? 'not saved' : 'discarded'}, so the `
    + `${followOns.length} follow-on batch(es) waiting on it were not sent (#590):`,
    followOns.map((f) => f.paths).flat(),
  );
}

/**
 * Send the writes an earlier page noted and never stored (#585).
 *
 * ── What it is for ──────────────────────────────────────────────────────────
 *
 * Saving is synchronous up to `note()` and asynchronous after it: the queue
 * awaits the core, a sequence number and an IndexedDB put before its record
 * exists. A reload, a typed URL or a closed tab in that window would take the
 * write with it, leaving a note the next page's #167 check could only report.
 * An in-app navigation does not: the queue lives at module scope.
 *
 * ── What it does ────────────────────────────────────────────────────────────
 *
 * For each note that carries a copy of its batch and was written **before this
 * page loaded**: if the queue already holds the batch, the put landed and only
 * the copy's release was lost, so the copy is dropped; if it does not, the
 * batch goes back into the queue under its own id, with the note's time.
 * Nothing is dispatched here — `startWriteQueue` drains after this resolves,
 * which is the one place that knows whose session this is (#282).
 *
 * **Why "before this page loaded" is enough of a fence.** A note newer than
 * that is this page's own write, mid-put. An older one could in principle be a
 * sibling tab's, mid-put at the instant this page looked; but this runs after
 * auth has resolved, so the sibling's put would have had to outlast a whole
 * page start. If it ever did, both tabs would send the same batch id with the
 * same fields — a redelivery, which the queue already accepts from
 * `reclaimStranded` and which `createdAt`'s enqueue-time instant survives
 * (#232) — not a loss.
 *
 * Runs once per page and never rejects: a copy the core refuses, or one too
 * malformed to read, is left as a bare note for the #167 check to report. Shared by
 * `startWriteQueue` and `SyncContext`'s check, which must not judge a note this
 * is about to send.
 */
export function recoverUnqueuedWrites() {
  if (!recovering) {
    recovering = recoverUnqueued().then(undefined, (err) => {
      console.error('[writes] recovering writes an earlier page left unstored failed:', err);
    });
  }
  return recovering;
}

async function recoverUnqueued() {
  const notes = outstandingWrites()
    .filter((r) => (r.batch || r.followOn) && r.batchId && r.at < loadedAt);
  if (!notes.length) return;
  const q = theQueue();
  const records = new Map((await q.records()).map((r) => [r.batchId, r]));
  const durable = await storeIsDurable();
  const restored = [];
  for (const n of notes) {
    const held = records.get(n.batchId);
    if (held) {
      if (durable && n.batch) releaseBatchCopy(n.batchId);
      // Dead, with its note still here: the page went between the refusal and
      // the settlement. The follow-on is dropped as the settlement would have
      // dropped it (#590); anything else rides on the batch's own settlement.
      if (held.state === 'dead') releaseFollowOn(n.batchId, 'dead');
      continue;
    }
    if (!n.batch) {
      // **A follow-on, no copy, and no batch (#590).** The copy goes only once
      // the queue has the batch, and the batch leaves the queue only when it
      // is acknowledged — a refused one stays, dead, until a teacher discards
      // it, and a discard settles in the same tick. So this is a batch the
      // server took, from a page that went before its settlement ran. What
      // was waiting on it goes now; the note itself is left for the
      // earlier-session check, which says what it can and cannot see.
      releaseFollowOn(n.batchId, 'done');
      continue;
    }
    const { ops, fields, subject } = n.batch;
    if (!Array.isArray(ops) || !Array.isArray(fields)) {
      console.error(
        `[writes] the note for batch ${n.batchId} carries a copy that is not a batch, so it `
        + 'cannot be resent and is left for the earlier-session check to report (#585).',
      );
      continue;
    }
    try {
      await q.restore({
        batchId: n.batchId, ops, fields, subject: subject ?? n.subject ?? null, enqueuedAt: n.at,
      });
      restored.push(n);
    } catch (err) {
      console.error(
        `[writes] the queue refused to take back batch ${n.batchId}, which an earlier page `
        + 'noted and never stored; it is left for the earlier-session check to report (#585):',
        err,
      );
    }
  }
  if (restored.length) {
    // Said, because it means a page closed mid-save — which is worth knowing
    // even though nothing was lost. What it saw: a note with a copy, older
    // than this page, and no batch in the queue.
    console.warn(
      `[writes] ${restored.length} write(s) were noted by an earlier page and not yet in the `
      + 'write queue when it closed, so they have been put back in the queue from the copy in '
      + 'the note and will be sent (#585):',
      restored.map((n) => n.paths).flat(),
    );
  }
}

/**
 * Put one batch of writes on the queue.
 *
 * Throws — synchronously, before anything durable happens — for a write the
 * model forbids or the codec cannot carry, so a malformed write fails at the
 * call site that wrote it rather than being stored and replayed.
 */
function encodeBatch(entries) {
  const refs = entries.map((e) => e.ref);
  const batchId = freeId('wq');
  const ops = [];
  const fields = [];
  entries.forEach((entry, index) => {
    rememberFirestore(entry.ref?.firestore);
    // `${batchId}#${index}` rather than the path: the core refuses a duplicate
    // op id across the whole queue, and one document is written by many batches
    // over a session. The *document's* id — the thing that makes a replay safe —
    // is in the path, minted once by `core/src/ids.rs` before it got here.
    const opId = `${batchId}#${index}`;
    ops.push({ id: opId, path: entry.ref.path, kind: entry.kind });
    if (entry.kind !== KIND.delete) fields.push(...encodeFields(opId, entry.data));
  });
  return {
    batchId,
    ops,
    fields,
    refs,
    subject: subjectForRefs(refs),
    paths: refs.map((r) => String(r?.path || '')).filter(Boolean),
  };
}

/**
 * `followOn` is another `writeBatch` facade, uncommitted: the batch to queue
 * once this one is acknowledged, and never if it is refused (#590). It is
 * encoded now and written onto this batch's note, so it is exactly as durable
 * as this batch from the first synchronous line — see `releaseFollowOn`.
 */
async function enqueue(entries, { followOn = null } = {}) {
  const { batchId, ops, fields, refs, subject } = encodeBatch(entries);
  const followOnEntries = followOn?.[ENTRIES]?.() || [];
  const next = followOnEntries.length ? encodeBatch(followOnEntries) : null;
  const id = note(batchId, refs, { ops, fields, subject }, next && {
    batchId: next.batchId,
    ops: next.ops,
    fields: next.fields,
    subject: next.subject,
    paths: next.paths,
  });
  try {
    if (!next) {
      return await theQueue().enqueue({
        batchId, ops, fields, subject,
      });
    }
    awaitingCaller.add(batchId);
    let answer;
    try {
      answer = await theQueue().enqueue({
        batchId, ops, fields, subject,
      });
    } finally {
      awaitingCaller.delete(batchId);
    }
    // Acknowledged while we waited, so the follow-on was queued in the same
    // tick: wait for it as for a second commit of the caller's own.
    // Its refusal is this caller's to show — but the note is not this
    // catch's to clear, so it is awaited outside it.
    const following = followOnWaits.get(batchId);
    followOnWaits.delete(batchId);
    if (following) {
      return following.then(() => answer);
    }
    return answer;
  } catch (error) {
    // The queue refused it, so nothing is owed and the note must not outlive
    // the attempt — a record left behind would count toward the chip forever.
    // By id rather than by batch: this is the one path where the batch never
    // became durable, so there is nothing for anybody else to settle.
    clearWrite(id);
    throw error;
  }
}

/** `setDoc`, through the queue. Same signature. */
export function setDoc(ref, data, options) {
  rememberFirestore(ref?.firestore);
  return enqueue([{ ref, kind: options?.merge ? KIND.merge : KIND.set, data }]);
}

/** `deleteDoc`, through the queue. Same signature. */
export function deleteDoc(ref) {
  rememberFirestore(ref?.firestore);
  return enqueue([{ ref, kind: KIND.delete }]);
}

/** How `enqueue` reads an uncommitted facade's entries for a follow-on (#590). */
const ENTRIES = Symbol('the entries of an uncommitted batch');

/**
 * `writeBatch`, through the queue.
 *
 * A facade rather than the SDK's `WriteBatch`: the refs are collected as they
 * go in, because by the time `commit()` rejects the batch's own mutations are
 * private to the SDK and there is nothing left to name. It collects the *data*
 * as well: a queued write has to outlive the object that described it.
 */
export function writeBatch(handle) {
  rememberFirestore(handle);
  const entries = [];
  const facade = {
    [ENTRIES]: () => entries.slice(),
    set(ref, data, options) {
      entries.push({ ref, kind: options?.merge ? KIND.merge : KIND.set, data });
      return facade;
    },
    /**
     * **Mapped onto a merge.** `update` and a merging `set` differ in one way
     * — `update` fails if the document does not exist, a merge creates it — and
     * the core's three kinds (set, merge, delete) have no room for a
     * precondition. So this facade's `update` creates a missing document where
     * the SDK's would fail.
     */
    update(ref, data) {
      entries.push({ ref, kind: KIND.merge, data });
      return facade;
    },
    delete(ref) {
      entries.push({ ref, kind: KIND.delete });
      return facade;
    },
    // `entries.slice()` because the batch is committed once and the array must
    // not keep growing under the queue if a caller reuses the facade.
    //
    // One record for the batch, not one per ref: a batch is atomic, so the
    // studio has one outstanding thing here and the chip should say one.
    //
    // `commit({ followOn })` hands over a second, uncommitted facade to be
    // queued when this one is acknowledged (#590) — see `releaseFollowOn`.
    commit: (options) => enqueue(entries.slice(), options),
  };
  return facade;
}

/** Test seam. Drops the queue and everything it was holding. */
export async function __resetWritesForTests() {
  if (queue) await queue.reset();
  queue = null;
  storeIsDurable = () => Promise.resolve(false);
  awaitingCaller.clear();
  followOnWaits.clear();
  // A reset is this module's stand-in for a reload, so the line between this
  // page's notes and an earlier page's moves with it.
  loadedAt = Date.now();
  recovering = null;
  firestore = null;
  if (stopWatchingConnectivity) stopWatchingConnectivity();
  stopWatchingConnectivity = null;
  if (siblings) siblings.close();
  siblings = null;
  warnedAboutHandle = false;
  sessionUid = null;
  sessionKnown = false;
  foreign.clear();
  listeners.clear();
}
